import React, { JSX } from "react";
import { Box, Typography } from "@mui/material";
import { tl } from "../../components/translate";
import { scaleFont } from "../../components/Print/Print";
import { rupeeDisplay } from "../../helpers/rupee";
import BillPrintAmounts from "./PrintBill/BillPrintAmounts";
import { BillType } from "../../interfaces/BillInterfaces";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";

export const BillSummaryRow = ({
  label,
  value,
  discountPercent,
  smallFont = false,
  noMarginTop = false,
  showVatPercent = false,
  isGrandTotal = false
}: {
  label: JSX.Element;
  value: string | number;
  showVatPercent?: boolean;
  discountPercent?: number;
  smallFont?: boolean;
  noMarginTop?: boolean;
  isGrandTotal?: boolean;
}): JSX.Element => (
  <Box mt={noMarginTop ? 0 : "0.1cm"}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}
    >
      <Typography
        sx={{
          fontSize: scaleFont("0.3cm", smallFont ? 0.85 : 1.15),
          fontWeight: isGrandTotal ? 600 : 500
        }}
      >
        {label}{" "}
        {showVatPercent && (
          <Box component="span" ml="0.3rem">
            13%
          </Box>
        )}
        {Boolean(discountPercent) && (
          <Box component="span" ml="0.3rem">
            {`${discountPercent}%`}
          </Box>
        )}
      </Typography>
      <Typography
        sx={{
          textAlign: "right",
          fontWeight: isGrandTotal ? 600 : 500,
          fontSize: scaleFont("0.3cm", smallFont ? 0.85 : 1.15)
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const BillPrintSummary = ({
  totalPerUnitTimesQty,
  netTotal,
  discount,
  taxAmount,
  discountPercent,
  smallFont = false,
  noMarginTop = false,
  billData,
  clientBalance = 0
}: {
  totalPerUnitTimesQty: number;
  discount: number;
  taxAmount: number;
  netTotal: number;
  discountPercent?: number;
  smallFont?: boolean;
  noMarginTop?: boolean;
  billData?: BillType;
  clientBalance: number;
}): JSX.Element => {
  const currentRc = useCurrentResourceCentre();
  const isVatRegistered = currentRc?.settings?.billingSettings?.vatRegistered || false;

  const hasNewTaxSummary = billData?.summary && "vatAmtWithRates" in billData.summary;

  const showLegacyVat =
    Object.keys(billData?.document?.summary?.taxAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  const showVatByRate =
    Object.keys(billData?.document?.summary?.vatAmtWithRates || {}).filter(
      (vatPct) => Number(vatPct) !== 0
    ).length > 0;

  return (
    <div>
      {!!billData?.summary?.taxableSales && isVatRegistered && (
        <BillSummaryRow
          label={<>Taxable Sales</>}
          value={rupeeDisplay(billData.summary.taxableSales.toFixed(2))}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}
      {!!billData?.summary?.exemptSales && isVatRegistered && (
        <BillSummaryRow
          label={<>Exempt Sales</>}
          value={rupeeDisplay(billData.summary.exemptSales.toFixed(2))}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}
      {Boolean(totalPerUnitTimesQty) && (
        <BillSummaryRow
          label={tl("billing.total")}
          value={rupeeDisplay(totalPerUnitTimesQty.toFixed(2))}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}
      {discount > 0 && (
        <BillSummaryRow
          label={tl("billing.discount")}
          value={rupeeDisplay(discount.toFixed(2 || 0))}
          discountPercent={discountPercent}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}
      {Boolean(netTotal) && isVatRegistered && (
        <BillSummaryRow
          label={tl("billing.taxableAmount")}
          value={rupeeDisplay(netTotal.toFixed(2))}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}

      {!hasNewTaxSummary &&
        showLegacyVat &&
        Object.keys(billData?.summary?.taxAmtWithRates || {})
          .filter((vatPct) => Number(vatPct) !== 0)
          .map((vatPct, index) => (
            <BillSummaryRow
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              label={<>VAT {vatPct}%</>}
              value={billData?.summary?.taxAmtWithRates?.[vatPct]?.toFixed(2) || ""}
              smallFont={smallFont}
              noMarginTop={noMarginTop}
            />
          ))}

      {hasNewTaxSummary &&
        showVatByRate &&
        Object.keys(billData?.summary?.vatAmtWithRates || {})
          .filter((vatPct) => Number(vatPct) !== 0)
          .map((vatPct, index) => (
            <BillSummaryRow
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              label={<>VAT {vatPct}%</>}
              value={billData?.summary?.vatAmtWithRates?.[vatPct]?.toFixed(2) || ""}
              smallFont={smallFont}
              noMarginTop={noMarginTop}
            />
          ))}

      {Boolean(taxAmount) && (
        <BillSummaryRow
          label={<>Tax Amount</>}
          value={rupeeDisplay(Number(taxAmount.toFixed(2)))}
          smallFont={smallFont}
          noMarginTop={noMarginTop}
        />
      )}

      {billData && (
        <BillPrintAmounts
          billData={billData}
          clientBalance={clientBalance}
          smallFont={smallFont}
          noMarginTop
        />
      )}
    </div>
  );
};

export default BillPrintSummary;
